import { create } from 'zustand';
import * as api from '../lib/api';
import type { Task } from '../lib/api';

interface TaskState {
  byTaskId: Record<string, Task>;
  loadChannelTasks: (channelId: string) => Promise<void>;
  loadGuildTasks: (guildId: string) => Promise<void>;
  loadMyTasks: () => Promise<Task[]>;
  upsertTask: (task: Task) => void;
  removeTask: (taskId: string) => void;
  purgeChannels: (channelIds: string[]) => void;
  reset: () => void;
}

let tasksGeneration = 0;
const guildTaskLoads = new Map<string, Promise<void>>();

export const useTaskStore = create<TaskState>()((set) => ({
  byTaskId: {},

  upsertTask: (task) =>
    set((s) => {
      const current = s.byTaskId[task.id];
      if (current && current.updated_at >= task.updated_at) {
        return s;
      }
      // Task mutations and thread lookups omit presentation fields that the
      // personal read model supplies (relationship, Project/channel context).
      // Preserve known fields while accepting the newer canonical task values.
      return { byTaskId: { ...s.byTaskId, [task.id]: { ...current, ...task } } };
    }),

  reset: () => {
    tasksGeneration++;
    guildTaskLoads.clear();
    set({ byTaskId: {} });
  },

  removeTask: (taskId) =>
    set((s) => {
      const next = { ...s.byTaskId };
      delete next[taskId];
      return { byTaskId: next };
    }),

  purgeChannels: (channelIds) =>
    set((s) => {
      tasksGeneration++;
      const removed = new Set(channelIds);
      const byTaskId = Object.fromEntries(
        Object.entries(s.byTaskId).filter(([, task]) => !removed.has(task.channel_id)),
      );
      return { byTaskId };
    }),

  loadChannelTasks: async (channelId) => {
    const generation = tasksGeneration;
    try {
      const { tasks } = await api.fetchChannelTasks(channelId);
      if (generation !== tasksGeneration) return;
      set((s) => {
        const next: Record<string, Task> = { ...s.byTaskId };
        for (const task of tasks) {
          const current = s.byTaskId[task.id];
          next[task.id] = current && current.updated_at >= task.updated_at ? current : task;
        }
        return { byTaskId: next };
      });
    } catch {
      // Failed refresh leaves previous snapshot in place.
    }
  },

  loadMyTasks: async () => {
    const generation = tasksGeneration;
    try {
      const { tasks } = await api.fetchMyTasks();
      if (generation !== tasksGeneration) return [];
      set((s) => ({
        byTaskId: { ...s.byTaskId, ...Object.fromEntries(tasks.map((task) => [task.id, task])) },
      }));
      return tasks;
    } catch {
      // Failed refresh leaves the previous authorization-filtered snapshot in place.
      return [];
    }
  },

  loadGuildTasks: async (guildId) => {
    const inFlight = guildTaskLoads.get(guildId);
    if (inFlight) return inFlight;

    const generation = tasksGeneration;
    const load = api.fetchGuildTasks(guildId).then(({ tasks }) => {
      if (generation !== tasksGeneration) return;
      set((s) => {
        const next: Record<string, Task> = { ...s.byTaskId };
        for (const task of tasks) {
          const current = s.byTaskId[task.id];
          next[task.id] = current && current.updated_at >= task.updated_at ? current : task;
        }
        return { byTaskId: next };
      });
    });
    guildTaskLoads.set(guildId, load);
    try {
      await load;
    } finally {
      if (guildTaskLoads.get(guildId) === load) guildTaskLoads.delete(guildId);
    }
  },
}));
